import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  HiArrowLongRight,
  HiOutlineCheck,
  HiOutlineChevronDown,
  HiOutlineHeart,
} from "react-icons/hi2";
import productData from "../Datas/productData";
import { CartContext } from "./CartContext";
import siteConfig from "../config/siteConfig";
import "../css/productdetails.scss";

const fallbackImage = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 900 1200">
    <rect width="900" height="1200" fill="#e9e7e0"/>
    <path d="M330 410h240v380H330z" fill="#d2d0c8"/>
    <text x="450" y="850" text-anchor="middle" font-family="Arial" font-size="24" fill="#555">IMAGE COMING SOON</text>
  </svg>
`)}`;

const formatPrice = (price) =>
  new Intl.NumberFormat(siteConfig.locale, {
    style: "currency",
    currency: siteConfig.currency.code,
    maximumFractionDigits: 0,
  }).format(price);

const titleCase = (value) =>
  value
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
    .replace("T Shirts", "T-Shirts");

const handleImageError = (event) => {
  event.currentTarget.onerror = null;
  event.currentTarget.src = fallbackImage;
};

const RecommendationCard = ({ product, onNavigate }) => (
  <article className="product-recommendation-card">
    <Link to={`/shopDetails/${product.id}`} onClick={onNavigate}>
      <span className="product-recommendation-card__media">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          onError={handleImageError}
        />
        {product.badge && <em>{product.badge}</em>}
      </span>
      <span className="product-recommendation-card__copy">
        <strong>{product.name}</strong>
        <span>{formatPrice(product.price)}</span>
      </span>
    </Link>
  </article>
);

const ProductDetail = () => {
  const { id } = useParams();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, isWishlist, toggleWishlist } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState(product?.size?.[0] || "");
  const [selectedColor, setSelectedColor] = useState(product?.colors?.[0] || "");
  const [justAdded, setJustAdded] = useState(false);

  useEffect(() => {
    setSelectedSize(product?.size?.[0] || "");
    setSelectedColor(product?.colors?.[0] || "");
    setJustAdded(false);
  }, [product]);

  useEffect(() => {
    if (!product || typeof document === "undefined") return undefined;
    const previousTitle = document.title;
    document.title = `${product.name} | ${siteConfig.brandName}`;
    return () => {
      document.title = previousTitle;
    };
  }, [product]);

  const recommendations = useMemo(() => {
    if (!product) return [];
    return productData
      .filter((item) => item.id !== product.id && item.gender === product.gender)
      .sort(
        (first, second) =>
          Number(second.category === product.category) -
          Number(first.category === product.category)
      )
      .slice(0, 4);
  }, [product]);

  if (!product) {
    return (
      <main className="aa-product aa-product--not-found" id="main-content">
        <section className="product-not-found">
          <span>404</span>
          <h1>Product not found</h1>
          <p>The piece you are looking for may no longer be available.</p>
          <Link to="/shop">Back to shop</Link>
        </section>
      </main>
    );
  }

  const sourceImages = product.images?.length ? product.images : [product.image];
  const galleryItems =
    sourceImages.length === 1
      ? [
          { src: sourceImages[0], view: "full", label: "full look" },
          { src: sourceImages[0], view: "detail", label: "fabric and fit detail" },
        ]
      : sourceImages.map((src, index) => ({
          src,
          view: index === 1 ? "detail" : "full",
          label: index === 0 ? "full look" : `view ${index + 1}`,
        }));

  const favorite = isWishlist(product.id);
  const canAddToCart = Boolean(selectedSize && selectedColor);

  const handleAddToCart = () => {
    if (!canAddToCart) return;
    addToCart(product.id, 1, selectedSize, selectedColor);
    setJustAdded(true);
  };

  const changeSize = (size) => {
    setSelectedSize(size);
    setJustAdded(false);
  };

  const changeColor = (color) => {
    setSelectedColor(color);
    setJustAdded(false);
  };

  const scrollToTop = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <main className="aa-product" id="main-content">
      <div className="product-layout">
        <section className="product-gallery" aria-label={`${product.name} gallery`}>
          {galleryItems.map((item, index) => (
            <figure
              className={`product-gallery__item product-gallery__item--${item.view}`}
              key={`${item.src}-${index}`}
            >
              <img
                src={item.src}
                alt={`${product.name}, ${item.label}`}
                loading={index === 0 ? "eager" : "lazy"}
                onError={handleImageError}
              />
            </figure>
          ))}
          <div className="product-gallery__counter" aria-hidden="true">
            {galleryItems.map((item, index) => (
              <span key={`${item.src}-dot-${index}`} />
            ))}
          </div>
        </section>

        <aside className="product-panel">
          <nav className="product-breadcrumb" aria-label="Breadcrumb">
            <Link to="/shop">Shop</Link>
            <span>/</span>
            <Link to={`/shop?gender=${product.gender}`}>{titleCase(product.gender)}</Link>
            <span>/</span>
            <Link to={`/shop?category=${product.category}`}>
              {titleCase(product.category)}
            </Link>
          </nav>

          <div className="product-panel__heading">
            <div>
              <span className="product-panel__badge">{product.badge}</span>
              <h1>{product.name}</h1>
            </div>
            <span className="product-panel__price">{formatPrice(product.price)}</span>
          </div>

          <div className="product-rating" aria-label={`${product.rating} out of 5 stars`}>
            <span aria-hidden="true">★★★★★</span>
            <span>{product.rating}</span>
            <a href="#product-details">{product.reviews} reviews</a>
          </div>

          <section className="product-option" aria-labelledby="color-label">
            <div className="product-option__heading">
              <h2 id="color-label">Color</h2>
              <span>{selectedColor}</span>
            </div>
            <div className="product-swatches">
              {product.colors.map((color, index) => (
                <button
                  type="button"
                  key={color}
                  className={selectedColor === color ? "is-selected" : ""}
                  aria-label={`Choose ${color}`}
                  aria-pressed={selectedColor === color}
                  onClick={() => changeColor(color)}
                >
                  <span
                    style={{ backgroundColor: product.colorHex[index] || "#aaa" }}
                    aria-hidden="true"
                  />
                  {selectedColor === color && <HiOutlineCheck aria-hidden="true" />}
                </button>
              ))}
            </div>
          </section>

          <section className="product-option product-option--sizes" aria-labelledby="size-label">
            <div className="product-option__heading">
              <h2 id="size-label">Choose size</h2>
              <Link to="/contact">Size guide</Link>
            </div>
            <div className="product-sizes">
              {product.size.map((size) => (
                <button
                  type="button"
                  key={size}
                  className={selectedSize === size ? "is-selected" : ""}
                  aria-pressed={selectedSize === size}
                  onClick={() => changeSize(size)}
                >
                  {size}
                </button>
              ))}
            </div>
            <p className="product-stock">
              <span aria-hidden="true" /> In stock — ships in 1–2 business days
            </p>
          </section>

          <div className="product-actions">
            <button
              type="button"
              className="product-add"
              onClick={handleAddToCart}
              disabled={!canAddToCart}
            >
              {justAdded ? "Added to bag" : "Add to bag"}
              <span>{formatPrice(product.price)}</span>
            </button>
            <button
              type="button"
              className={`product-wishlist ${favorite ? "is-active" : ""}`}
              aria-label={`${favorite ? "Remove from" : "Add to"} wishlist`}
              aria-pressed={favorite}
              onClick={() => toggleWishlist(product.id)}
            >
              <HiOutlineHeart aria-hidden="true" />
            </button>
          </div>

          <div className="product-add-status" aria-live="polite">
            {justAdded && (
              <span>
                Your item is in the bag.
                <Link to="/cartpage">View bag</Link>
              </span>
            )}
          </div>

          <ul className="product-promises" aria-label="Shopping benefits">
            <li>
              Free US shipping over {formatPrice(siteConfig.commerce.freeShippingThreshold)}
            </li>
            <li>{siteConfig.commerce.returnWindowDays}-day returns</li>
            <li>Secure checkout</li>
          </ul>

          <div className="product-accordions" id="product-details">
            <details open>
              <summary>
                Product details
                <HiOutlineChevronDown aria-hidden="true" />
              </summary>
              <div>
                <p>{product.description}</p>
                <ul>
                  {product.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
              </div>
            </details>
            <details>
              <summary>
                Material &amp; care
                <HiOutlineChevronDown aria-hidden="true" />
              </summary>
              <div>
                <p>{product.material}</p>
                <dl>
                  {Object.entries(product.attributes).map(([label, value]) => (
                    <React.Fragment key={label}>
                      <dt>{label}</dt>
                      <dd>{value}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </div>
            </details>
            <details>
              <summary>
                Shipping &amp; returns
                <HiOutlineChevronDown aria-hidden="true" />
              </summary>
              <div>
                <p>
                  Orders are prepared within 1–2 business days. Unworn items may
                  be returned within 30 days of delivery.
                </p>
                <div className="product-accordion-links">
                  <Link to="/shipping">Shipping information</Link>
                  <Link to="/returns">Returns policy</Link>
                </div>
              </div>
            </details>
          </div>
        </aside>
      </div>

      <section className="product-recommendations" aria-labelledby="recommendations-heading">
        <div className="product-recommendations__heading">
          <h2 id="recommendations-heading">You may also like</h2>
          <Link to={`/shop?gender=${product.gender}`} onClick={scrollToTop}>
            Shop all
            <HiArrowLongRight aria-hidden="true" />
          </Link>
        </div>
        <div className="product-recommendations__grid">
          {recommendations.map((item) => (
            <RecommendationCard
              key={item.id}
              product={item}
              onNavigate={scrollToTop}
            />
          ))}
        </div>
      </section>

      <div className="product-mobile-atc">
        <div>
          <strong>{product.name}</strong>
          <span>{formatPrice(product.price)}</span>
        </div>
        {justAdded ? (
          <Link to="/cartpage">View bag</Link>
        ) : (
          <button type="button" onClick={handleAddToCart} disabled={!canAddToCart}>
            Add to bag
          </button>
        )}
      </div>
    </main>
  );
};

export default ProductDetail;
